import { supabase } from '@/integrations/supabase/client';
import { format, startOfMonth, endOfMonth, parseISO, differenceInDays, eachDayOfInterval } from 'date-fns';

export interface MonthlyReportStats {
  monthLabel: string;
  year: number;
  totalBooksFinished: number;
  totalPages: number;
  totalFocusMinutes: number;
  readingDays: number;
  totalDaysInMonth: number;
  avgPagesPerDay: number;
  maxPagesInADay: number;
  booksRead: Array<{
    id: string;
    title: string;
    authors: string[];
    coverUrl: string | null;
    rating: number | null;
    pageCount: number | null;
    startedAt: string | null;
    finishedAt: string | null;
    daysToRead: number | null;
  }>;
  fastestBook: { title: string; authors: string[]; coverUrl: string | null; daysToRead: number } | null;
  longestBook: { title: string; pageCount: number; coverUrl: string | null } | null;
  topRatedBook: { title: string; rating: number; coverUrl: string | null } | null;
}

export async function fetchMonthlyReport(userId: string, date: Date): Promise<MonthlyReportStats> {
  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);
  const monthStartStr = format(monthStart, 'yyyy-MM-dd');
  const monthEndStr = format(monthEnd, 'yyyy-MM-dd');

  // Fetch books finished this month
  const { data: userBooks } = await supabase
    .from('user_books')
    .select(`
      id,
      rating_star,
      finished_at,
      started_at,
      current_page,
      book_id,
      books!user_books_book_id_fkey (
        id, title, authors, page_count, cover_url, cover_cache_url
      )
    `)
    .eq('user_id', userId)
    .eq('shelf', 'read')
    .gte('finished_at', monthStartStr)
    .lte('finished_at', monthEndStr);

  // Fetch focus sessions this month
  const { data: focusSessions } = await supabase
    .from('focus_sessions')
    .select('actual_duration_seconds, started_at, was_completed')
    .eq('user_id', userId)
    .gte('started_at', monthStart.toISOString())
    .lte('started_at', monthEnd.toISOString())
    .eq('was_completed', true);

  // Fetch reading activities this month to determine reading days and daily pages
  const { data: activities } = await supabase
    .from('activities')
    .select('created_at, type, payload')
    .eq('user_id', userId)
    .gte('created_at', monthStart.toISOString())
    .lte('created_at', monthEnd.toISOString())
    .in('type', ['reading_progress_updated', 'book_finished']);

  const books = userBooks || [];
  const totalDaysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd }).length;

  // Calculate reading days from activities
  const readingDaySet = new Set<string>();
  (activities || []).forEach(a => {
    readingDaySet.add(format(parseISO(a.created_at), 'yyyy-MM-dd'));
  });
  // Also add focus session days
  (focusSessions || []).forEach(s => {
    readingDaySet.add(format(parseISO(s.started_at), 'yyyy-MM-dd'));
  });

  // Calculate daily pages from activities
  const dailyPages: Record<string, number> = {};
  (activities || []).forEach(a => {
    const day = format(parseISO(a.created_at), 'yyyy-MM-dd');
    const payload = a.payload as any;
    const increment = payload?.increment || payload?.pages_increment || 0;
    dailyPages[day] = (dailyPages[day] || 0) + increment;
  });

  const maxPagesInADay = Object.values(dailyPages).length > 0
    ? Math.max(...Object.values(dailyPages))
    : 0;

  const totalPages = books.reduce((sum, b) => sum + (b.books?.page_count || 0), 0);
  const totalFocusMinutes = (focusSessions || []).reduce(
    (sum, s) => sum + Math.round((s.actual_duration_seconds || 0) / 60), 0
  );

  const booksRead = books.map(b => {
    const daysToRead = b.started_at && b.finished_at
      ? Math.max(1, differenceInDays(parseISO(b.finished_at), parseISO(b.started_at)))
      : null;
    return {
      id: b.book_id,
      title: b.books?.title || 'Unknown',
      authors: b.books?.authors || [],
      coverUrl: b.books?.cover_cache_url || b.books?.cover_url || null,
      rating: b.rating_star,
      pageCount: b.books?.page_count || null,
      startedAt: b.started_at,
      finishedAt: b.finished_at,
      daysToRead,
    };
  });

  const booksWithDays = booksRead.filter(b => b.daysToRead !== null);
  const fastestBook = booksWithDays.length > 0
    ? booksWithDays.reduce((min, b) => (b.daysToRead! < min.daysToRead! ? b : min))
    : null;

  const booksWithPages = booksRead.filter(b => b.pageCount && b.pageCount > 0);
  const longestBook = booksWithPages.length > 0
    ? booksWithPages.reduce((max, b) => (b.pageCount! > max.pageCount! ? b : max))
    : null;

  const booksWithRating = booksRead.filter(b => b.rating && b.rating > 0);
  const topRatedBook = booksWithRating.length > 0
    ? booksWithRating.reduce((max, b) => (b.rating! > max.rating! ? b : max))
    : null;

  return {
    monthLabel: format(date, 'MMMM'),
    year: date.getFullYear(),
    totalBooksFinished: books.length,
    totalPages,
    totalFocusMinutes,
    readingDays: readingDaySet.size,
    totalDaysInMonth,
    avgPagesPerDay: readingDaySet.size > 0 ? Math.round(totalPages / readingDaySet.size) : 0,
    maxPagesInADay,
    booksRead,
    fastestBook: fastestBook ? {
      title: fastestBook.title,
      authors: fastestBook.authors,
      coverUrl: fastestBook.coverUrl,
      daysToRead: fastestBook.daysToRead!,
    } : null,
    longestBook: longestBook ? {
      title: longestBook.title,
      pageCount: longestBook.pageCount!,
      coverUrl: longestBook.coverUrl,
    } : null,
    topRatedBook: topRatedBook ? {
      title: topRatedBook.title,
      rating: topRatedBook.rating!,
      coverUrl: topRatedBook.coverUrl,
    } : null,
  };
}
